import { supabase } from "~/integrations/supabase/client";
import { resolveGross } from "~/utils/iva";

export interface CreditUtilizationData {
  utilized: number;
  available: number;
  percentage: number;
  salesRequestUtilized: number;
  manualFacturasUtilized: number;
  uncollectedChecksAmount: number; // Amount from pending checks only (not in_portfolio)
  portfolioChecksAmount: number; // Amount from checks in portfolio (for display only)
  insuredAmount: number;
  totalPendingAmount: number;
}

/**
 * Calculates complete credit utilization for a client including sales_request, manual_facturas, and uncollected checks
 * @param clientId The client ID to calculate credit utilization for
 * @returns Promise<CreditUtilizationData> Complete credit utilization data
 */
export const calculateCreditUtilization = async (
  clientId: string
): Promise<CreditUtilizationData> => {
  try {
    // First, get client's insured amount
    const { data: clientData, error: clientError } = await supabase
      .from("clients")
      .select("insured_amount")
      .eq("id", clientId)
      .is("deleted_at", null) // Exclude soft-deleted clients
      .maybeSingle();

    if (clientError) throw clientError;

    const insuredAmount = clientData?.insured_amount || 0;

    // Get all unpaid sales requests for this client (accepted and beyond)
    const { data: salesRequestsData, error: salesRequestsError } =
      await supabase
        .from("sales_request")
        .select("price_with_iva, total_amount, is_exento")
        .eq("client_id", clientId)
        .in("status", ["accepted", "in_transit", "delivered"]) // Include all statuses from accepted onwards
        .eq("paid", false);

    if (salesRequestsError) throw salesRequestsError;

    const salesRequestUtilized = salesRequestsData.reduce(
      (total, request) => total + resolveGross(request),
      0
    );

    // Get all manual_facturas with positive balance for this client
    const { data: manualFacturasData, error: manualFacturasError } =
      await supabase
        .from("manual_facturas")
        .select("balance")
        .eq("client_id", clientId)
        .gt("balance", 0); // Only get positive balances (unpaid amounts)

    if (manualFacturasError) throw manualFacturasError;

    const manualFacturasUtilized = manualFacturasData.reduce(
      (total, factura) => total + (Number(factura.balance) || 0),
      0
    );

    // Get uncollected checks for this client (pending only - affects credit)
    // Only truly uncollected checks (pending) should restrict credit
    // Checks 'in_portfolio' (cheques en cartera) should NOT affect credit line
    // Only 'collected' or 'paid' checks should release credit
    const { data: uncollectedChecksData, error: checksError } = await supabase
      .from("payments")
      .select("amount")
      .eq("client", clientId)
      .eq("payment_method", "check")
      .eq("check_status", "pending"); // Only pending checks restrict credit, not in_portfolio

    if (checksError) throw checksError;

    const uncollectedChecksAmount = uncollectedChecksData.reduce(
      (total, check) => total + (Number(check.amount) || 0),
      0
    );

    // Get checks in portfolio for display purposes (don't affect credit)
    const { data: portfolioChecksData, error: portfolioError } = await supabase
      .from("payments")
      .select("amount")
      .eq("client", clientId)
      .eq("payment_method", "check")
      .eq("check_status", "in_portfolio"); // Checks in portfolio for display

    if (portfolioError) throw portfolioError;

    const portfolioChecksAmount = portfolioChecksData.reduce(
      (total, check) => total + (Number(check.amount) || 0),
      0
    );

    // Total utilized credit includes only sales requests, manual facturas, and pending checks
    // Cheques en cartera (in_portfolio) do NOT affect credit line
    const totalUtilized =
      salesRequestUtilized + manualFacturasUtilized + uncollectedChecksAmount;
    const totalPendingAmount = totalUtilized;
    const availableCredit = Math.max(0, insuredAmount - totalUtilized);
    const utilizationPercentage =
      insuredAmount > 0
        ? Math.min(100, (totalUtilized / insuredAmount) * 100)
        : 0;

    return {
      utilized: totalUtilized,
      available: availableCredit,
      percentage: utilizationPercentage,
      salesRequestUtilized,
      manualFacturasUtilized,
      uncollectedChecksAmount, // Include pending checks amount (not in_portfolio)
      portfolioChecksAmount, // Include portfolio checks amount for display
      insuredAmount,
      totalPendingAmount,
    };
  } catch (error) {
    console.error("Error calculating credit utilization:", error);
    throw error;
  }
};

/**
 * Gets all outstanding payments for a client including both sales_request and manual_facturas
 * @param clientId The client ID to get outstanding payments for
 * @returns Promise with outstanding amounts from both sources
 */
export const getClientOutstandingPayments = async (clientId: string) => {
  try {
    // Get unpaid sales requests
    const { data: salesRequestsData, error: salesRequestsError } =
      await supabase
        .from("sales_request")
        .select(
          "id, price_with_iva, payment_due_date, status, created_at, factura_creation_date"
        )
        .eq("client_id", clientId)
        .eq("status", "accepted")
        .eq("paid", false);

    if (salesRequestsError) throw salesRequestsError;

    // Get manual facturas with positive balance
    const { data: manualFacturasData, error: manualFacturasError } =
      await supabase
        .from("manual_facturas")
        .select(
          "id, balance, payment_due_date, doc_number, doc_type, created_at"
        )
        .eq("client_id", clientId)
        .gt("balance", 0);

    if (manualFacturasError) throw manualFacturasError;

    // NEW: Get uncollected checks as part of outstanding payments
    // Only truly uncollected checks (pending) are considered outstanding
    // Checks 'in_portfolio' (cheques en cartera) are not considered outstanding for credit purposes
    const { data: uncollectedChecksData, error: uncollectedChecksError } =
      await supabase
        .from("payments")
        .select(
          "id, amount, check_number, bank, collection_date, check_status, created_at"
        )
        .eq("client", clientId)
        .eq("payment_method", "check")
        .eq("check_status", "pending"); // Only pending checks, not in_portfolio

    if (uncollectedChecksError) throw uncollectedChecksError;

    return {
      salesRequests: salesRequestsData || [],
      manualFacturas: manualFacturasData || [],
      uncollectedChecks: uncollectedChecksData || [], // NEW: Include only pending checks
    };
  } catch (error) {
    console.error("Error getting client outstanding payments:", error);
    throw error;
  }
};

/**
 * Gets all late payments for a client including both sales_request and manual_facturas
 * @param clientId The client ID to get late payments for
 * @returns Promise with late payments from both sources
 */
export const getClientLatePayments = async (clientId?: string) => {
  try {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const salesRequestQuery = supabase
      .from("sales_request")
      .select("*, client:clients(name, tax_id)")
      .eq("status", "accepted")
      .eq("paid", false)
      .lt("payment_due_date", today.toISOString());

    const manualFacturasQuery = supabase
      .from("manual_facturas")
      .select(
        `
        *,
        clients!inner (
          name,
          tax_id
        )
      `
      )
      .gt("balance", 0)
      .lt("payment_due_date", today.toISOString());

    // NEW: Get overdue checks (past collection_date but still pending)
    // Only pending checks can be overdue for credit purposes, not in_portfolio
    const overdueChecksQuery = supabase
      .from("payments")
      .select(
        `
        *,
        clients(name, tax_id)
      `
      )
      .eq("payment_method", "check")
      .eq("check_status", "pending")
      .lt("collection_date", today.toISOString());

    // Add client filter if provided
    if (clientId) {
      salesRequestQuery.eq("client_id", clientId);
      manualFacturasQuery.eq("client_id", clientId);
      overdueChecksQuery.eq("client", clientId);
    }

    const [salesRequestsResult, manualFacturasResult, overdueChecksResult] =
      await Promise.all([
        salesRequestQuery,
        manualFacturasQuery,
        overdueChecksQuery,
      ]);

    if (salesRequestsResult.error) throw salesRequestsResult.error;
    if (manualFacturasResult.error) throw manualFacturasResult.error;
    if (overdueChecksResult.error) throw overdueChecksResult.error;

    return {
      salesRequests: salesRequestsResult.data || [],
      manualFacturas: manualFacturasResult.data || [],
      overdueChecks: overdueChecksResult.data || [], // NEW: Include overdue checks
    };
  } catch (error) {
    console.error("Error getting late payments:", error);
    throw error;
  }
};

/**
 * Validates if a client with 7-day credit can create new sales requests
 *
 * BUSINESS LOGIC:
 * - For 7-day credit conditions (maximum_days = 7):
 *   ❌ BLOCKS: Only if there are overdue orders (past payment_due_date) OR protested checks
 *   ✅ ALLOWS: Multiple concurrent orders within the 7-day grace period
 *
 * - For other credit conditions: Always allows (returns canCreate: true)
 *
 * EXAMPLES:
 * - Order created today with 7-day credit → ✅ ALLOWED (new orders can be created)
 * - Order created 5 days ago with 7-day credit → ✅ ALLOWED (still within grace period)
 * - Order created 8 days ago with 7-day credit, unpaid → ❌ BLOCKED (overdue)
 * - Client with protested checks → ❌ BLOCKED (regardless of timing)
 *
 * @param clientId The client ID to validate
 * @param salesConditionMaxDays The maximum days for the sales condition (should be 7 for this validation)
 * @returns Promise<{canCreate: boolean, reason?: string, overdueCount?: number}> Validation result
 */
export const validate7DayCreditEligibility = async (
  clientId: string,
  salesConditionMaxDays: number
) => {
  // Only apply this validation for 7-day credit conditions
  if (salesConditionMaxDays !== 7) {
    return { canCreate: true };
  }

  try {
    const outstandingPayments = await getClientOutstandingPayments(clientId);
    const now = new Date();

    // Check for overdue sales requests (past payment_due_date)
    const overdueSalesRequests = outstandingPayments.salesRequests.filter(
      (request) => {
        const dueDate = new Date(request.payment_due_date);
        return request.status === "accepted" && dueDate < now;
      }
    );

    // Check for overdue manual facturas (past payment_due_date)
    const overdueManualFacturas = outstandingPayments.manualFacturas.filter(
      (factura) => {
        if (!factura.payment_due_date) return false;
        const dueDate = new Date(factura.payment_due_date);
        return factura.balance > 0 && dueDate < now;
      }
    );

    // NEW: Check for protested checks (should always block)
    const protestedChecks = await supabase
      .from("payments")
      .select("id, check_number, amount, check_status")
      .eq("client", clientId)
      .eq("payment_method", "check")
      .eq("check_status", "protested"); // English status value

    if (protestedChecks.error) throw protestedChecks.error;

    // If there are protested checks, always block
    if (protestedChecks.data && protestedChecks.data.length > 0) {
      return {
        canCreate: false,
        reason: "protested_checks",
        protestedChecksCount: protestedChecks.data.length,
        protestedChecks: protestedChecks.data,
      };
    }

    const totalOverdueCount =
      overdueSalesRequests.length + overdueManualFacturas.length;

    if (totalOverdueCount > 0) {
      return {
        canCreate: false,
        reason: "overdue_payments",
        overdueCount: totalOverdueCount,
        overdueSalesRequests,
        overdueManualFacturas,
      };
    }

    // Optional: Check for too many concurrent orders (uncomment if needed)
    /*
    const maxConcurrentOrders = 3;
    const currentUnpaidCount = outstandingPayments.salesRequests.length +
                              outstandingPayments.manualFacturas.length;

    if (currentUnpaidCount >= maxConcurrentOrders) {
      return {
        canCreate: false,
        reason: "too_many_concurrent_orders",
        currentCount: currentUnpaidCount,
        maxAllowed: maxConcurrentOrders,
      };
    }
    */

    // All checks passed
    return {
      canCreate: true,
      currentUnpaidCount:
        outstandingPayments.salesRequests.length +
        outstandingPayments.manualFacturas.length,
      uncollectedChecksCount:
        outstandingPayments.uncollectedChecks?.length || 0, // NEW: Include check count
    };
  } catch (error) {
    console.error("Error in validate7DayCreditEligibility:", error);
    return {
      canCreate: false,
      reason: "validation_error",
      error: error.message,
    };
  }
};
